import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tooltip,
  Typography,
} from "antd";
import { InfoCircleOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  ALTURA_PREVIEW_MAX,
  ALTURA_PREVIEW_MIN,
  configOptionApi,
  corregirFormatoCodec,
  VIDEO_OPT_DEFAULTS,
  VideoOptimizationSettings,
} from "../../services/configOption.api";
import InfoAyuda, { AyudaBloque } from "../../Components/InfoAyuda";
import { invalidateVideoPlayerConfig } from "../../utils/videoPlayerConfig";
// Bridge antd estático: los métodos `Modal.x` / `message.x` /
// `notification.x` importados directo de "antd" no funcionan en
// v5 + React 19 (root React huérfano fuera del ConfigProvider).
// Round 2026-05-01 — sweep general post-fix /sueldos.
import { getMsg } from "../../utils/antdStatic";

const { Title, Paragraph, Text } = Typography;

/** Resoluciones habituales de la versión liviana, en px del lado más corto (atajos al lado del campo). */
const ALTURAS_HABITUALES = [360, 480, 720];

/** Etiqueta de un campo con su ayuda breve (tooltip). */
const Etiqueta = ({ texto, ayuda }: { texto: string; ayuda: string }) => (
  <Space>
    <span>{texto}</span>
    <Tooltip title={ayuda}>
      <InfoCircleOutlined />
    </Tooltip>
  </Space>
);

/**
 * Explicación COMPLETA del sistema de videos (el (i) junto al título). Cada afirmación sale de
 * docs/config-optimizacion-videos.md y del servicio `videoOptimization.service.js`: si el
 * comportamiento cambia, hay que cambiar esto también.
 */
const AyudaVideos = () => (
  <InfoAyuda titulo="Cómo funcionan los videos" texto="¿Cómo funciona?" testId="ayuda-videos">
    <AyudaBloque titulo="Qué es">
      Cada video que se sube pasa por tres etapas: se guarda, el servidor lo optimiza y se muestra
      con un reproductor que gasta pocos datos. Esta pestaña controla las dos últimas. Solo el
      administrador cambia estos ajustes; los clientes, las aseadoras y los demás usuarios
      únicamente ven los videos que les corresponden.
    </AyudaBloque>

    <AyudaBloque titulo="1. La subida">
      Se pueden subir videos desde las Notas del aseo, Tareas, Inventario, Flujos de caja y
      Finanzas empresa, la información para el huésped de las propiedades, la Agenda de contactos
      y Costos sugeridos. Ningún video puede durar más de <b>20 minutos</b> (límite fijo: no se
      configura aquí) y el servidor acepta hasta <b>3 GB</b> por archivo, aunque algunas
      pantallas ponen un tope menor (25 MB en Tareas, Inventario y Finanzas empresa; 100 MB en
      los adjuntos de Flujos, información para el huésped, Agenda de contactos y Costos
      sugeridos). En las Notas del aseo la subida es <b>reanudable</b>: va en trozos de 8 MB y,
      si se corta la señal, continúa donde quedó.
    </AyudaBloque>

    <AyudaBloque titulo="2. La optimización en el servidor">
      El video se guarda primero tal cual (ya se puede ver) y se crea su miniatura. Después, en
      segundo plano, el servidor lo recomprime con ffmpeg usando los ajustes de esta pestaña.
      Procesa <b>un video a la vez</b>, en orden de llegada: un video corto tarda unos minutos y
      uno de 20 minutos tarda bastante más (el servidor corta un proceso que pase de 60 minutos,
      por defecto, y conserva el original). Mientras tanto se ve el original; al
      terminar, el contenido se reemplaza sin que cambie el enlace. Si algo falla, queda el
      original.
      <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
        <li>
          <b>No se agranda un video que ya es eficiente</b>: si el original ya se reproduce en
          los navegadores y la recompresión no ahorra al menos un 2 %, se conserva el original.
        </li>
        <li>
          Un video que los navegadores no pueden reproducir (como los HEVC del iPhone) siempre
          se convierte, aunque el resultado pese más: que se vea importa más que el peso.
        </li>
        <li>
          En videos de más de 5 minutos el servidor usa como mucho el preset «veryfast», aunque
          aquí elijas uno más lento, para no saturarse.
        </li>
        <li>
          <b>«720p» se mide por el lado más corto</b>, como en YouTube: con 720, un video
          horizontal de 1920×1080 queda en 1280×720 y uno vertical de celular de 1080×1920
          queda en 720×1280. Lo mismo vale para la versión liviana (480 = 854×480 en
          horizontal y 480×854 en vertical). Nunca se agranda un video.
        </li>
        <li>
          <b>Formato y códec</b>: MP4 admite H.264, H.265 y VP9 (con audio AAC); WebM solo admite
          VP9 (con audio Opus). Esta pestaña no deja combinar WebM con H.264 o H.265: al elegir
          uno de los dos, el otro se ajusta solo y se avisa. VP9 tarda bastante más en codificar
          que H.264.
        </li>
        <li>
          El original no se guarda aparte: lo optimizado reemplaza al original y es definitivo.
        </li>
        <li>
          Si apagas «Compresión activa», los videos se guardan tal cual y tampoco se generan
          versiones livianas.
        </li>
      </ul>
    </AyudaBloque>

    <AyudaBloque titulo="3. La versión liviana y el reproductor">
      Después de optimizar, el servidor crea una copia pequeña del video (480p por defecto, 24
      fotogramas por segundo y audio mono) que el reproductor usa para gastar muy pocos datos. No
      se crea si el video pesa menos de 3 MB, si su lado más corto ya mide eso o menos, o si la copia no
      ahorra lo suficiente (si pesara el 85 % o más del original). Sobre el video aparece un
      botón <b>Baja / HD</b> para cambiar de calidad; la elección de cada persona se recuerda en
      su propio dispositivo. «Calidad con la que se abre el reproductor» solo decide con cuál
      parte quien todavía no eligió (y si su navegador pide ahorrar datos, parte en Baja). Un
      video sin versión liviana (antiguo, de poco peso o que aún se está procesando) se ve en su
      calidad original y no muestra el botón.
    </AyudaBloque>

    <AyudaBloque titulo="Quién ve qué">
      Los clientes, las aseadoras y los demás usuarios ven sus videos con el mismo reproductor.
      No pueden abrir esta pantalla ni el módulo Almacenamiento.
    </AyudaBloque>

    <AyudaBloque titulo="Cómo se gestiona el espacio">
      El módulo <b>Almacenamiento</b> (solo administradores) muestra cuánto pesa cada archivo
      (el video, su miniatura y su versión liviana), permite eliminar con confirmación, muestra el
      consumo de datos y trae el botón «Generar versiones livianas» para los videos que ya
      existían.
    </AyudaBloque>

    <AyudaBloque titulo="Qué NO hace">
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        <li>
          Los ajustes de compresión (formato, códec, resolución, calidad…) solo se aplican a los
          videos nuevos: los que ya estaban subidos no se vuelven a comprimir.
        </li>
        <li>
          Cambiar la resolución de la versión liviana no regenera las que ya existen; el botón de
          Almacenamiento solo crea la de los videos que no tienen.
        </li>
        <li>
          Apagar la versión liviana no borra las que ya existen: se siguen usando.
        </li>
        <li>No se puede cambiar el límite de 20 minutos desde esta pantalla.</li>
      </ul>
    </AyudaBloque>
  </InfoAyuda>
);

/**
 * Pestaña admin para configurar la compresión BACKEND de videos y su versión liviana. A
 * diferencia de la de imágenes, no hay preview side-by-side: transcodear video en el browser
 * no es viable. Los parámetros de compresión aplican a los videos que se suban DESDE AHORA; los
 * videos viejos NO se reprocesan (la compresión es destructiva al momento del upload). Lo único
 * que se puede generar después, para los videos que ya existen, es la versión liviana: se hace
 * desde /almacenamiento.
 *
 * Ver docs/config-optimizacion-videos.md.
 */
export default function VideoOptimizationTab() {
  const [form] = Form.useForm<VideoOptimizationSettings>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState(false);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState<VideoOptimizationSettings>(VIDEO_OPT_DEFAULTS);

  // Aviso corto cuando elegir el formato o el códec ajustó al otro (WebM solo admite VP9).
  const [avisoCombinacion, setAvisoCombinacion] = useState<string | null>(null);
  const formatoElegido = Form.useWatch("format", form);

  const compresionActiva = Form.useWatch("enabled", form);
  const versionLivianaActiva = Form.useWatch("previewEnabled", form);
  // La altura de la versión liviana no hace nada si la compresión o la función están apagadas.
  const sinVersionLiviana = compresionActiva === false || versionLivianaActiva === false;

  const load = useCallback(async () => {
    setLoading(true);
    setErrorCarga(false);
    const res = await configOptionApi.getVideoOptimization();
    if (res?.data) {
      // Un servidor que aún no manda algún campo no deja el control vacío: cae al default.
      // Una combinación incoherente (WebM con H.264) no se muestra tal cual: se lleva a una
      // válida, igual que hace el servidor al leerla.
      const valores = { ...VIDEO_OPT_DEFAULTS, ...res.data };
      const combinacion = corregirFormatoCodec(valores, "codec");
      form.setFieldsValue({ ...valores, format: combinacion.format, codec: combinacion.codec });
      setAvisoCombinacion(combinacion.aviso);
      setDefaults({ ...VIDEO_OPT_DEFAULTS, ...(res.defaults ?? {}) });
    } else {
      setErrorCarga(true);
    }
    setLoading(false);
  }, [form]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSave = async (values: VideoOptimizationSettings) => {
    setSaving(true);
    const res = await configOptionApi.updateVideoOptimization(values);
    if (res?.data) {
      form.setFieldsValue({ ...VIDEO_OPT_DEFAULTS, ...res.data });
      setAvisoCombinacion(null);
      // El reproductor guarda la configuración 5 min en sessionStorage: se limpia para que tome
      // la calidad predeterminada nueva sin esperar.
      invalidateVideoPlayerConfig();
    }
    setSaving(false);
  };

  /**
   * Formato y códec van de la mano: WebM solo admite VP9. Al elegir WebM el códec pasa a VP9; al
   * elegir H.264/H.265 estando en WebM, el formato vuelve a MP4. Así la combinación inválida no
   * se puede armar ni mandar.
   */
  const onValuesChange = (cambiados: Partial<VideoOptimizationSettings>) => {
    const cambio = "format" in cambiados ? "format" : "codec" in cambiados ? "codec" : null;
    if (!cambio) return;
    const { format, codec, aviso } = corregirFormatoCodec(
      { format: form.getFieldValue("format"), codec: form.getFieldValue("codec") },
      cambio,
    );
    form.setFieldsValue({ format, codec });
    setAvisoCombinacion(aviso);
  };

  const restoreDefaults = () => {
    form.setFieldsValue(defaults);
    setAvisoCombinacion(null);
    getMsg()?.info("Valores por defecto cargados en el formulario. Pulsa «Guardar» para guardarlos.");
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (errorCarga) {
    return (
      <div style={{ maxWidth: 720 }}>
        <Alert
          type="error"
          showIcon
          message="No se pudo leer la configuración de videos"
          description="Falló la consulta, así que no se muestran valores para no mostrar datos falsos."
          action={
            <Button onClick={() => void load()} data-testid="video-opt-reintentar">
              Reintentar
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <Space align="center" wrap style={{ marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>
          Optimización de videos
        </Title>
        <AyudaVideos />
      </Space>
      <Paragraph type="secondary">
        Los videos se comprimen en el <strong>servidor</strong> con ffmpeg después de subirlos; el
        navegador <strong>no degrada</strong> la calidad al subir. El proceso es asíncrono: el
        video se guarda primero tal cual (ya se puede ver) y el servidor lo optimiza después,{" "}
        <strong>de a un video por vez</strong>. Un video corto tarda unos minutos y uno de 20
        minutos (el máximo permitido, no configurable) tarda bastante más. Si algo falla, queda el
        original.
      </Paragraph>

      <Alert
        type="info"
        showIcon
        message="Qué se aplica a los videos que ya estaban subidos"
        description={
          <>
            Los ajustes de compresión (formato, códec, resolución, calidad…) solo se aplican a los{" "}
            <strong>videos nuevos</strong>: los ya subidos no se vuelven a comprimir. Lo único que
            se puede generar después para ellos es la <strong>versión liviana</strong>, y se hace
            desde Almacenamiento, donde también está el consumo de datos.{" "}
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              onClick={() => navigate("/almacenamiento")}
              data-testid="video-opt-ir-almacenamiento"
            >
              Ir a Almacenamiento
            </Button>
          </>
        }
        style={{ marginBottom: 16 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={onSave}
        onValuesChange={onValuesChange}
        initialValues={VIDEO_OPT_DEFAULTS}
      >
        <Card size="small" style={{ marginBottom: 16 }}>
          <Form.Item
            name="enabled"
            label={
              <Etiqueta
                texto="Compresión activa"
                ayuda="Si la apagas, los videos nuevos se guardan tal cual los sube la persona (más peso), no se recomprimen y tampoco se genera su versión liviana. Los videos ya optimizados no cambian."
              />
            }
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>
        </Card>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="format"
              label={
                <Etiqueta
                  texto="Formato (contenedor)"
                  ayuda="MP4 es el más compatible (todos los navegadores). WebM comprime mejor pero tiene menos soporte, y solo admite el códec VP9 (con audio Opus): al elegirlo, el códec pasa a VP9."
                />
              }
              rules={[{ required: true, message: "Requerido" }]}
            >
              <Select
                options={[
                  { value: "mp4", label: "MP4 (recomendado)" },
                  { value: "webm", label: "WebM" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="codec"
              label={
                <Etiqueta
                  texto="Codec de video"
                  ayuda="H.264: máxima compatibilidad. H.265 (HEVC): ~30 % menos peso, pero con soporte limitado (Safari antiguo). VP9: el único que admite WebM (también sirve en MP4); tarda bastante más en codificar. H.264 y H.265 solo van en MP4."
                />
              }
              rules={[{ required: true, message: "Requerido" }]}
              extra={
                formatoElegido === "webm"
                  ? "WebM usa siempre VP9, con el audio en Opus."
                  : undefined
              }
            >
              <Select
                options={[
                  { value: "h264", label: "H.264 (recomendado)" },
                  { value: "h265", label: "H.265 / HEVC (menos peso)" },
                  { value: "vp9", label: "VP9 (obligatorio en WebM)" },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        {avisoCombinacion && (
          <Alert
            type="info"
            showIcon
            closable
            onClose={() => setAvisoCombinacion(null)}
            message={avisoCombinacion}
            style={{ marginBottom: 16 }}
            data-testid="video-opt-aviso-combinacion"
          />
        )}

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="maxHeight"
              label={
                <Etiqueta
                  texto="Resolución máxima (px del lado más corto)"
                  ayuda="Como el «720p» de YouTube, se mide por el lado MÁS CORTO del video: 720 = el lado corto mide 720 px, tanto en horizontal (1920×1080 pasa a 1280×720) como en vertical (1080×1920 pasa a 720×1280). 1080 = Full HD; 720 = HD (recomendado); 480 = SD. Se mantiene la proporción y los videos más chicos NO se agrandan."
                />
              }
              rules={[
                { required: true, message: "Requerido" },
                { type: "number", min: 240, max: 4320, message: "Entre 240 y 4320" },
              ]}
            >
              <Select
                options={[
                  { value: 480, label: "480 (SD)" },
                  { value: 720, label: "720 (HD, recomendado)" },
                  { value: 1080, label: "1080 (Full HD)" },
                  { value: 1440, label: "1440 (2K)" },
                  { value: 2160, label: "2160 (4K)" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="fps"
              label={
                <Etiqueta
                  texto="Fotogramas por segundo"
                  ayuda="30 es lo estándar para video web; 24 es estilo cine. Más fotogramas = más fluido pero más peso."
                />
              }
              rules={[
                { required: true, message: "Requerido" },
                { type: "number", min: 15, max: 60, message: "Entre 15 y 60" },
              ]}
            >
              <InputNumber min={15} max={60} step={1} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="crf"
              label={
                <Etiqueta
                  texto="Calidad (CRF)"
                  ayuda="Rango 18-32. Más bajo = mejor calidad y más peso. 18 ≈ visualmente sin pérdida, 23 es un valor razonable, desde 28 ya se nota la pérdida."
                />
              }
              rules={[
                { required: true, message: "Requerido" },
                { type: "number", min: 18, max: 32, message: "Entre 18 y 32" },
              ]}
            >
              <InputNumber min={18} max={32} step={1} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="audioBitrate"
              label={
                <Etiqueta
                  texto="Bitrate del audio (kbps)"
                  ayuda="128 kbps es lo estándar, 192 es calidad alta y 64 es suficiente para voz."
                />
              }
              rules={[
                { required: true, message: "Requerido" },
                { type: "number", min: 64, max: 320, message: "Entre 64 y 320" },
              ]}
            >
              <Select
                options={[
                  { value: 64, label: "64 kbps (voz)" },
                  { value: 96, label: "96 kbps" },
                  { value: 128, label: "128 kbps (recomendado)" },
                  { value: 192, label: "192 kbps (alta)" },
                  { value: 256, label: "256 kbps" },
                  { value: 320, label: "320 kbps (máxima)" },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="preset"
          label={
            <Etiqueta
              texto="Preset (velocidad de codificación)"
              ayuda="Más rápido = el servidor tarda menos pero comprime peor. Más lento = mejor compresión a igual calidad, pero con más CPU y más tiempo. «medium» es el equilibrio. Con VP9 (WebM) hace el mismo papel: ajusta la velocidad del codificador."
            />
          }
          rules={[{ required: true, message: "Requerido" }]}
          extra="En los videos de más de 5 minutos el servidor usa como mucho «veryfast», aunque elijas uno más lento, para no saturarse."
        >
          <Select
            options={[
              { value: "ultrafast", label: "ultrafast (peor compresión)" },
              { value: "superfast", label: "superfast" },
              { value: "veryfast", label: "veryfast" },
              { value: "faster", label: "faster" },
              { value: "fast", label: "fast" },
              { value: "medium", label: "medium (equilibrado, recomendado)" },
              { value: "slow", label: "slow" },
              { value: "slower", label: "slower" },
              { value: "veryslow", label: "veryslow (máxima compresión)" },
            ]}
          />
        </Form.Item>

        <Card
          size="small"
          style={{ marginBottom: 16 }}
          title="Versión liviana para ver con poco consumo"
          data-testid="video-opt-seccion-liviana"
        >
          <Paragraph type="secondary" style={{ marginTop: 0 }}>
            Es una copia pequeña de cada video que el reproductor usa por defecto para gastar muy
            pocos datos (sobre todo con datos móviles). El original se conserva y se puede pedir
            con el botón <Text code>HD</Text> que aparece sobre el video.
          </Paragraph>

          <Form.Item
            name="previewEnabled"
            label={
              <Etiqueta
                texto="Generar la versión liviana"
                ayuda="Cada video nuevo recibe una copia pequeña justo después de optimizarse. Si la apagas, los videos nuevos no la reciben (las que ya existen no se borran y se siguen usando). Necesita la compresión activa."
              />
            }
            valuePropName="checked"
            extra={
              compresionActiva === false
                ? "Con la compresión apagada tampoco se generan versiones livianas."
                : undefined
            }
          >
            <Switch disabled={compresionActiva === false} />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="previewHeight"
                label={
                  <Etiqueta
                    texto="Resolución de la versión liviana (px del lado más corto)"
                    ayuda={`Entre ${ALTURA_PREVIEW_MIN} y ${ALTURA_PREVIEW_MAX}. Igual que la resolución máxima, se mide por el lado MÁS CORTO: 480 = un video horizontal queda 854×480 y uno vertical 480×854. 360 = muy liviana, 480 = recomendada, 720 = casi HD. Un video más chico no se agranda y las medidas siempre quedan pares.`}
                  />
                }
                rules={[
                  { required: true, message: "Requerido" },
                  {
                    type: "number",
                    min: ALTURA_PREVIEW_MIN,
                    max: ALTURA_PREVIEW_MAX,
                    message: `Entre ${ALTURA_PREVIEW_MIN} y ${ALTURA_PREVIEW_MAX}`,
                  },
                ]}
                extra={
                  <>
                    <div>
                      Cambiarla NO regenera las versiones livianas que ya existen: solo las nuevas
                      usan el valor nuevo.
                    </div>
                    <Space size={4} wrap style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Atajos:
                      </Text>
                      {ALTURAS_HABITUALES.map((h) => (
                        <Button
                          key={h}
                          size="small"
                          disabled={sinVersionLiviana}
                          onClick={() => form.setFieldValue("previewHeight", h)}
                          data-testid={`video-opt-altura-${h}`}
                        >
                          {h}
                        </Button>
                      ))}
                    </Space>
                  </>
                }
              >
                <InputNumber
                  min={ALTURA_PREVIEW_MIN}
                  max={ALTURA_PREVIEW_MAX}
                  step={10}
                  precision={0}
                  disabled={sinVersionLiviana}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="playerDefaultQuality"
                label={
                  <Etiqueta
                    texto="Calidad con la que se abre el reproductor"
                    ayuda="Es la calidad con la que parte cada video que tiene versión liviana. Cada persona puede cambiarla con el botón sobre el video y su elección se recuerda en su propio dispositivo. Si su navegador pide ahorrar datos y no ha elegido, parte en Baja."
                  />
                }
                rules={[{ required: true, message: "Requerido" }]}
                extra="Cada persona puede cambiarla en el propio video; su elección se recuerda en su dispositivo."
              >
                <Select
                  options={[
                    { value: "baja", label: "Baja (ahorra datos)" },
                    { value: "alta", label: "Alta (original)" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Space wrap>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={saving}
            data-testid="video-opt-guardar"
          >
            Guardar configuración
          </Button>
          <Button onClick={restoreDefaults} icon={<ReloadOutlined />}>
            Restaurar defaults
          </Button>
        </Space>
      </Form>

      <Card size="small" style={{ marginTop: 24 }} title="Cómo probar">
        <Paragraph style={{ marginBottom: 8 }}>Después de guardar parámetros nuevos:</Paragraph>
        <ol style={{ marginTop: 0, paddingLeft: 20 }}>
          <li>
            Sube un video corto (de varios MB) desde las Notas del aseo, Tareas, Inventario o
            Finanzas empresa.
          </li>
          <li>El video aparece de inmediato con su calidad original.</li>
          <li>
            El servidor lo optimiza cuando le toca su turno (de a un video por vez, así que si hay
            otros en cola espera): un video corto tarda unos minutos y uno largo, bastante más.
            Cuando termina, el contenido se reemplaza y se crea la versión liviana. Refresca la
            galería para ver el resultado.
          </li>
          <li>
            Mira el peso final en Almacenamiento (la columna Peso suma el video, su miniatura y su
            versión liviana). Si el original ya era eficiente se deja igual. El registro del
            servidor muestra <Text code>[videoOpt] OK</Text> con el peso final.
          </li>
          <li>
            Si el peso no te satisface, ajusta <Text code>crf</Text> o la resolución máxima (<Text code>maxHeight</Text>, lado más corto){" "}
            y vuelve a probar con un video nuevo: los ya subidos no se recomprimen.
          </li>
        </ol>
      </Card>
    </div>
  );
}
